import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { GridColDef, GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';

import { RefreshIcon } from 'assets/icons';
import CustomDataGrid from 'components/common/CustomDataGrid';
import moment from 'moment';
import { useGetLeadsQuery, useGetOutboundCampaignIdsQuery } from 'services/api/leads.api';
import { ExtendedLead } from 'services/types/lead';

import DownloadLeadsDialog from './DownloadLeadsDialog';

const last24hoursDate = new Date();
last24hoursDate.setDate(last24hoursDate.getDate() - 1);

const formatDateValue = (value: Date | null) => {
  if (!value) return 'No';
  const dateValue = new Date(value);
  if (dateValue > last24hoursDate) return dateValue.toLocaleTimeString('es-CL');
  return `${dateValue.getDate()}/${dateValue.getMonth() + 1} ${dateValue.getHours()}:${dateValue
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;
};

const getAttributes = (outboundCampaignIds: string[]): GridColDef<ExtendedLead>[] => {
  return [
    {
      field: 'client.businessUnit.name',
      headerName: 'Unidad Negocio',
      width: 120,
      valueGetter: (_, row) => row.client?.businessUnit.name,
      filterable: false,
      sortable: false,
    },
    {
      field: 'client.id',
      headerName: 'Cliente',
      width: 70,
      valueGetter: (_, row) => row.client?.id,
    },
    {
      field: 'client.name',
      headerName: 'Nombre',
      width: 200,
      valueGetter: (_, row) => `${row.client?.firstName || ''} ${row.client?.lastName || ''}`.trim(),
    },
    {
      field: 'client.phone',
      headerName: 'Teléfono',
      width: 120,
      valueGetter: (_, row) => row.client?.phone || '',
    },
    {
      field: 'source',
      headerName: 'Origen',
      width: 180,
    },
    {
      field: 'outboundCampaignId',
      headerName: 'Campaña',
      width: 130,
      type: 'singleSelect',
      valueOptions: outboundCampaignIds,
    },
    {
      field: 'CRMUpdateAt',
      headerName: 'Subido CRM',
      width: 100,
      valueFormatter: (value: Date | null) => formatDateValue(value),
    },
    {
      field: 'initialBrandOfInterest',
      headerName: 'Marca interés',
      width: 120,
    },
    {
      field: 'initialModelOfInterest',
      headerName: 'Modelo interés',
      width: 120,
    },
    {
      field: 'createdAt',
      headerName: 'Creación',
      width: 100,
      type: 'date',
      valueFormatter: (value: Date) => formatDateValue(value),
    },
    {
      field: 'messagesCount',
      headerName: 'Mensajes',
      width: 90,
    },
    {
      field: 'discarded',
      headerName: 'Descartado',
      width: 90,
      type: 'boolean',
      valueFormatter: (value: boolean) => (value ? 'Si' : 'No'),
    },
  ];
};

interface LeadsProps {
  businessUnitId: number | null;
}

const Leads: FC<LeadsProps> = ({ businessUnitId }) => {
  const navigate = useNavigate();
  const [outboundCampaignFilterAnchorElement, setOutboundCampaignFilterAnchorElement] = useState<null | HTMLElement>(
    null
  );
  const queryParams = new URLSearchParams(window.location.search);

  const currentDate = new Date();
  const dateBeginningOfMonth = moment(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
  const dateEndOfMonth = moment(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  const queryFilterModel = queryParams.get('filterModel');
  const queryPaginationModel = queryParams.get('paginationModel');
  const querySortModel = queryParams.get('sortModel');
  const queryHighlightedClientId = queryParams.get('highlightedClientId');
  const parsedQueryPaginationModel = queryPaginationModel
    ? (JSON.parse(queryPaginationModel) as GridPaginationModel)
    : null;
  const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
    parsedQueryPaginationModel && parsedQueryPaginationModel.pageSize <= 100 && parsedQueryPaginationModel.page >= 0
      ? parsedQueryPaginationModel
      : {
          page: 0,
          pageSize: 50,
        }
  );
  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    queryFilterModel
      ? (JSON.parse(queryFilterModel) as GridFilterModel)
      : {
          items: [
            {
              field: 'createdAt',
              operator: 'greaterThan',
              value: dateBeginningOfMonth.toISOString(),
            },
            {
              field: 'createdAt',
              operator: 'lessThan',
              value: dateEndOfMonth.toISOString(),
            },
          ],
        }
  );
  const [sortModel, setSortModel] = useState<GridSortModel>(
    querySortModel ? (JSON.parse(querySortModel) as GridSortModel) : []
  );

  const { data, refetch, isLoading } = useGetLeadsQuery({
    paginationModel: JSON.stringify(paginationModel),
    filterModel: JSON.stringify(filterModel),
    sortModel: JSON.stringify(sortModel),
    businessUnitId: businessUnitId || -1,
  });

  const { data: outboundCampaignIds } = useGetOutboundCampaignIdsQuery({
    businessUnitId: businessUnitId || -1,
    startDate: null,
    endDate: null,
  });

  const { rows: leads, count: total } = data || { rows: [], count: 0 };

  useEffect(() => {
    refetch().catch(() => {
      // eslint-disable-next-line no-console
      console.error('Error fetching leads');
    });
  }, [paginationModel, businessUnitId, filterModel, sortModel]);

  useEffect(() => {
    queryParams.delete('paginationModel');
    // save pagination in query params
    queryParams.set('paginationModel', JSON.stringify(paginationModel));
    navigate(`?${queryParams.toString()}`, { replace: true });
  }, [paginationModel]);

  useEffect(() => {
    queryParams.delete('filterModel');
    // save filters in query params
    if (filterModel.items.length > 0) {
      queryParams.set('filterModel', JSON.stringify(filterModel));
    }
    navigate(`?${queryParams.toString()}`, { replace: true });
  }, [filterModel]);

  useEffect(() => {
    queryParams.delete('sortModel');
    // save sort in query params
    if (sortModel.length > 0) {
      queryParams.set('sortModel', JSON.stringify(sortModel));
    }
    navigate(`?${queryParams.toString()}`, { replace: true });
  }, [sortModel]);

  const columns = getAttributes(outboundCampaignIds || []);

  const handleFilterSelect = (field: string, operator: string, value: string | number | boolean | null) => {
    const newFilterItems = filterModel.items.filter((item) => item.field !== field);

    if (value !== null) {
      newFilterItems.push({
        field,
        operator,
        value,
      });
    }

    setFilterModel({
      ...filterModel,
      items: newFilterItems,
    });
  };

  const handleFilterClick = (
    event: React.MouseEvent<HTMLElement>,
    setAnchorEl: (element: HTMLElement | null) => void
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = (setAnchorEl: (element: HTMLElement | null) => void) => {
    setAnchorEl(null);
  };

  const handleOutboundCampaignFilterSelect = (filterType: string) => {
    if (filterType === 'all') {
      handleFilterSelect('outboundCampaignId', 'equals', null);
    } else {
      handleFilterSelect('outboundCampaignId', 'equals', filterType);
    }
    handleFilterClose(setOutboundCampaignFilterAnchorElement);
  };

  const navigateToClientId = (id: number, newPage = false) => {
    queryParams.set('highlightedClientId', id.toString());
    navigate(`?${queryParams.toString()}`, { replace: true });
    if (newPage) {
      window.open(`/clients/${id}`, '_blank');
      return;
    }
    navigate(`/clients/${id}`);
  };

  return (
    <Box style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Menu
        anchorEl={outboundCampaignFilterAnchorElement}
        open={Boolean(outboundCampaignFilterAnchorElement)}
        onClose={() => handleFilterClose(setOutboundCampaignFilterAnchorElement)}
      >
        <MenuItem onClick={() => handleOutboundCampaignFilterSelect('all')}>Ver todos</MenuItem>
        {outboundCampaignIds?.map((id) => (
          <MenuItem key={id} onClick={() => handleOutboundCampaignFilterSelect(id)}>
            {id}
          </MenuItem>
        ))}
      </Menu>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <CustomDataGrid<ExtendedLead>
          rows={leads}
          columns={columns}
          filterModel={filterModel}
          sortModel={sortModel}
          onFilterModelChange={setFilterModel}
          onSortModelChange={setSortModel}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={total}
          onRowClick={(params, event) => navigateToClientId(Number(params.client?.id), event.ctrlKey || event.metaKey)}
          getRowClassName={(params) => (params.client?.id === Number(queryHighlightedClientId) ? 'previous' : '')}
          pageId="leads"
          topBar={
            <Box display="flex" alignItems="center" justifyContent="flex-start" width="100%" overflow="scroll">
              {isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Tooltip title="Recargar">
                  <RefreshIcon
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      refetch().catch(() => {
                        // eslint-disable-next-line no-console
                        console.error('Error fetching leads');
                      });
                    }}
                    color="disabled"
                  />
                </Tooltip>
              )}
              <Button sx={{ ml: 2 }} onClick={(e) => handleFilterClick(e, setOutboundCampaignFilterAnchorElement)}>
                Filtrar Campaña
              </Button>
              <Button onClick={() => setOpenDownloadDialog(true)}>Descargar leads</Button>
            </Box>
          }
        />
      </Box>
      <DownloadLeadsDialog
        open={openDownloadDialog}
        onClose={() => setOpenDownloadDialog(false)}
        businessUnitId={businessUnitId || -1}
        filterModel={JSON.stringify(filterModel)}
        sortModel={JSON.stringify(sortModel)}
        totalLeads={total}
      />
    </Box>
  );
};

export default Leads;
